import React, { useState, useEffect } from "react";
import UserSearch from "./UserSearch";
import UserList from "./UserList";
import UserEdit from "./UserEdit";
import DeletedUserList from "./DeletedUserList"; // New component for deleted users
import Sidebar from "../Sidebar";
import Navbar from "../navbar";

const User = () => {
  const [users, setUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]); // State for deleted users
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [customerId, setCustomerId] = useState(""); // New state for customer ID
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false); // For loader
  const [message, setMessage] = useState(""); // For user search result message
  const [activeTab, setActiveTab] = useState("users"); // State to control the active tab
  const [selectedDeletedUser, setSelectedDeletedUser] = useState(null); // State for the selected deleted user

  const subscriptionStatuses = ["Active", "Expired", "Cancelled"]; // Example statuses

  // Fetch users from the API based on search, filter, and customerId
  const fetchUsers = async (searchTerm = "", status = "", customerId = "") => {
    setLoading(true); // Start loading
    setMessage(""); // Reset message

    try {
      const response = await fetch(`/api/users?search=${searchTerm}&status=${status}&customerId=${customerId}`);
      const data = await response.json();
      setUsers(data);

      if (data.length === 0) {
        setMessage("No users found.");
      } else {
        setMessage(`${data.length} user(s) found.`);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setMessage("Error fetching users.");
    } finally {
      setLoading(false); // Stop loading
    }
  };

  // Fetch deleted users from the API
  const fetchDeletedUsers = async () => {
    try {
      const response = await fetch(`/api/deleted-users`);
      const data = await response.json();
      setDeletedUsers(data);
    } catch (error) {
      console.error("Error fetching deleted users:", error);
    }
  };

  // Fetch a deleted user by ID
  const fetchDeletedUserById = async (userId) => {
    try {
      const response = await fetch(`/api/deleted-users/${userId}`);
      const data = await response.json();

      if (response.ok) {
        setSelectedDeletedUser(data); // Set the selected deleted user data
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error('Error fetching deleted user:', error);
    }
  };

  useEffect(() => {
    fetchUsers(); // Fetch users on mount
    fetchDeletedUsers(); // Fetch deleted users on mount
  }, []);

  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1 p-6">
        <Navbar />

        {/* User Search Section */}
        <UserSearch
          search={search}
          setSearch={setSearch}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          customerId={customerId}
          setCustomerId={setCustomerId}
          subscriptionStatuses={subscriptionStatuses}
          fetchUsers={fetchUsers}
          loading={loading}
          message={message}
        />

        {/* Tabs for switching between User List and Deleted User List */}
        <div className="mt-4 border-b border-gray-200">
          <nav className="flex space-x-4">
            <button
              className={`px-4 py-2 font-semibold ${
                activeTab === "users" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-600 hover:text-blue-500"
              }`}
              onClick={() => setActiveTab("users")}
            >
              User List
            </button>
            <button
              className={`px-4 py-2 font-semibold ${
                activeTab === "deletedUsers" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-600 hover:text-blue-500"
              }`}
              onClick={() => setActiveTab("deletedUsers")}
            >
              Deleted User List
            </button>
          </nav>
        </div>

        {/* Conditional rendering based on active tab */}
        {activeTab === "users" && (
          <>
            <UserList
              filteredUsers={users}
              setSelectedUser={setSelectedUser}
            />
            {selectedUser && (
              <UserEdit selectedUser={selectedUser} />
            )}
          </>
        )}

        {activeTab === "deletedUsers" && (
          <>
            <DeletedUserList
              deletedUsers={deletedUsers}
              fetchUsers={fetchUsers} // Pass fetchUsers to refresh user list
              setDeletedUsers={setDeletedUsers} // Set state for deleted users
              fetchDeletedUserById={fetchDeletedUserById} // Pass fetch function to DeletedUserList
              setSelectedDeletedUser={setSelectedDeletedUser} // Function to set selected deleted user
            />
            {selectedDeletedUser && (
              <UserEdit selectedUser={selectedDeletedUser} /> // Display UserEdit for the deleted user
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default User;
